import type {ChartData} from "@mantine/charts";
import {Bar, BarChart as BarChartRecharts, CartesianGrid, Label, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import {getNormalisedYAxisLabelsFromChartSeries} from "~/components/charts/chartSeriesHelpers";
import type {ChartSeries} from "~/components/reports/widgetSpecific/types";
import {graphColors} from "~/constants/graphColors";
import {graphsXAxisTickFormatter} from "~/components/reports/utilities";
import {useAnankePreferences} from "@vesta/ananke/contexts/anankeClientContext";
import {NumberSystems} from "~/utilities/typeDefinitions";
import {numberToHumanFriendlyStringWithNumberSystem} from "~/utilities/utilities";

export function BarChart({
    chartData,
    chartSeries,
    xAxisKey,
    xAxisTitle,
    scalingFactor,
    height,
    maxYValue,
    maxYValueSecondary,
}: {
    chartData: ChartData;
    chartSeries: Array<ChartSeries>;
    xAxisKey: string;
    xAxisTitle: string;
    scalingFactor: number;
    height: number;
    maxYValue: number;
    maxYValueSecondary: number | null;
}) {
    const yAxisLabels = getNormalisedYAxisLabelsFromChartSeries(chartSeries);
    const {theme} = useAnankePreferences();

    return (
        <ResponsiveContainer
            width="100%"
            height={height}
        >
            <BarChartRecharts
                data={chartData}
                margin={{top: 20, right: 20, bottom: 20, left: 20}}
                barGap={"6%"}
                maxBarSize={32}
            >
                <XAxis
                    dataKey={xAxisKey}
                    scale="auto"
                    tickFormatter={(value) => graphsXAxisTickFormatter(value)}
                    tickMargin={20}
                    tickSize={0}
                    tick={{
                        fontSize: "10px",
                    }}
                    axisLine={false}
                >
                    <Label
                        value={xAxisTitle}
                        dy={28}
                        style={{
                            textAnchor: "middle",
                            fontSize: "10px",
                        }}
                    />
                </XAxis>

                <CartesianGrid
                    horizontal={true}
                    vertical={false}
                    stroke={theme === "dark" ? "#ADACAC" : "#262626"}
                    strokeDasharray="7 5"
                />

                {yAxisLabels.left !== null && (
                    <YAxis
                        yAxisId={"left"}
                        orientation={"left"}
                        tickFormatter={(value) => {
                            return `${numberToHumanFriendlyStringWithNumberSystem(value, NumberSystems.indian)}`;
                        }}
                        tickSize={0}
                        tickMargin={4}
                        tick={{
                            fontSize: "10px",
                        }}
                        axisLine={false}
                        domain={[0, maxYValue]}
                    >
                        <Label
                            value={yAxisLabels.left.join(", ")}
                            position={"insideLeft"}
                            offset={0}
                            angle={-90}
                            style={{
                                textAnchor: "middle",
                                fontSize: "10px",
                            }}
                        />
                    </YAxis>
                )}

                {yAxisLabels.right !== null && (
                    <YAxis
                        yAxisId={"right"}
                        orientation={"right"}
                        tickFormatter={(value) => {
                            return `${numberToHumanFriendlyStringWithNumberSystem(value, NumberSystems.indian)}`;
                        }}
                        tickSize={0}
                        tickMargin={4}
                        tick={{
                            fontSize: "10px",
                        }}
                        axisLine={false}
                        domain={maxYValueSecondary === null ? undefined : [0, maxYValueSecondary]}
                    >
                        <Label
                            value={yAxisLabels.right.join(", ")}
                            position={"insideRight"}
                            offset={0}
                            angle={90}
                            style={{
                                textAnchor: "middle",
                                fontSize: "10px",
                            }}
                        />
                    </YAxis>
                )}

                <Tooltip
                    contentStyle={{
                        color: theme === "dark" ? "#C7C7C7" : "#292a2e",
                        background: theme === "dark" ? "#1F1F1F" : "#ffffff",
                        border: theme === "dark" ? "1px solid #383838" : "1px solid #0b120e24",
                        borderRadius: "4px",
                        fontSize: "14px",
                    }}
                    labelFormatter={(value) => {
                        return graphsXAxisTickFormatter(value);
                    }}
                    cursor={{
                        fill: theme === "dark" ? "#2B2B2B" : "#E5E5E5",
                    }}
                    formatter={(value) => {
                        const number = parseFloat(value.toString());
                        return Number.isInteger(number) === true ? number : number.toFixed(2);
                    }}
                />

                <Legend
                    verticalAlign="top"
                    align="right"
                    wrapperStyle={{
                        top: "-4px",
                        fontSize: "10px",
                    }}
                />

                {chartSeries.map((series, seriesIndex) => {
                    return (
                        <Bar
                            dataKey={series.name}
                            fill={graphColors[seriesIndex % graphColors.length]}
                            key={seriesIndex}
                            yAxisId={series.yAxisId}
                        />
                    );
                })}
            </BarChartRecharts>
        </ResponsiveContainer>
    );
}
